import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import WaterSiteDetail, { WaterSite } from './WaterSiteDetail';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import WaterSiteEditModal from './WaterSiteEditModal';
import { PortalWithState } from 'react-portal';
import Button from '../Button/Button';
import RecycledWaterInspectionCreateModal from '../RecycledWater/RecycledWaterInspectionCreateModal';
import { gql } from '@/modes/waterSupplier/gql';
import RequirePerms from '@/components/RequirePerms';

import { Link } from 'react-router-dom';
import useMode from '../../hooks/useMode';

export const waterSiteFields = gql(`
  fragment WaterSiteFields on WaterSite {
    id
    addressId
    name
    siteNumber
    type
    lastSurveyDate
    nextSurveyDate
    surveyIntervalYears
    lastInspectionDate
    nextInspectionDate
    ownerProfileId
    owner {
      id
      name
      firstName
      lastName
      emails {
        subType
        value
      }
      phones {
        subType
        value
        extension
      }
    }
    contactProfileId
    contact {
      id
      name
      firstName
      lastName
      emails {
        subType
        value
      }
      phones {
        subType
        value
        extension
      }
    }
    supervisor {
      id
      name
      firstName
      lastName
      emails {
        subType
        value
      }
      phones {
        subType
        value
        extension
      }
    }
    assignedToProfileId
    assignedTo {
      id
      name
      firstName
      lastName
      emails {
        subType
        value
      }
      phones {
        subType
        value
        extension
      }
    }
    recycledWaterServices {
      id
      siteNumber
      meterNumber
      sourceType
      pin
      type
      status
    }
    hazardWaterServices {
      id
      siteNumber
      meterNumber
      sourceType
      pin
      type
      status
    }
    address(required: true) {
      id
      street1
      street2
      city
      state
      zip
      zipExt
    }
    #      waterSites(where: $where) {
    #        id
    #        siteNumber
    #        meterNumber
    #        meterSize
    #        pin
    #        type
    #        status
    #      }
  }
`);

export const GET_WATER_SITE = gql(
  `
  query GetWaterSite($where: JSON) {
    waterSites(where: $where) {
      ...WaterSiteFields
    }
  }
`,
  [waterSiteFields]
);

type WaterSiteThirdPaneProps = {
  waterSiteId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
};

export default function WaterSiteThirdPane({
  waterSiteId,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: WaterSiteThirdPaneProps): JSX.Element {
  const mode = useMode();
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    waterSites: WaterSite[];
  }>(GET_WATER_SITE, {
    skip: !waterSiteId,
    variables: { where: { id: waterSiteId || null } },
  });

  const [waterSite, setWaterSite] = useState<WaterSite>();

  useEffect(() => {
    if (itemData && itemData?.waterSites?.length) {
      setWaterSite(itemData?.waterSites[0]);
    }
  }, [waterSiteId, itemData, setWaterSite]);

  if (itemError) {
    throw itemError;
  }
  return (
    <ThirdPane width={384 * 2} visible={!!waterSiteId}>
      {waterSite && (
        <ThirdPaneHeader
          onToggle={onToggle}
          title={waterSite ? waterSite?.name : 'Loading...'}
          onPrev={onPrev}
          onNext={onNext}
          prevEnabled={prevEnabled}
          nextEnabled={nextEnabled}
        >
          <RequirePerms waterSites="UPDATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    mr={10}
                    icon="edit"
                    variant="outline"
                    onClick={openPortal}
                  >
                    Edit
                  </Button>
                  {portal(
                    <WaterSiteEditModal
                      onClose={closePortal}
                      action="edit"
                      waterSiteId={waterSite.id}
                      waterSite={waterSite}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
          {waterSite.type === 'RECYCLED' && (
            <RequirePerms waterSites="UPDATE">
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <Button mr={10} variant="outline" onClick={openPortal}>
                      Create Inspection
                    </Button>
                    {portal(
                      <RecycledWaterInspectionCreateModal
                        onClose={closePortal}
                        waterSiteId={waterSite.id}
                        siteAddressId={waterSite.address.id}
                        rwInspection={{
                          outboundAssignment: waterSite.assignedTo
                            ? {
                                vendorProfileId: waterSite.assignedTo.id,
                              }
                            : undefined,
                          supervisorProfileId: waterSite?.supervisor?.id,
                          contactProfileId: waterSite?.contact?.id,
                        }}
                      />
                    )}
                  </>
                )}
              </PortalWithState>
            </RequirePerms>
          )}
          {waterSite.type === 'HAZARD' && (
            <RequirePerms waterSites="UPDATE">
              <Link
                to={`/${mode}/hazard-surveys/new?waterSiteId=${waterSite.id}`}
              >
                <Button mr={10} variant="outline">
                  New Hazard Survey
                </Button>
              </Link>
            </RequirePerms>
          )}
        </ThirdPaneHeader>
      )}
      <ThirdPaneContent>
        {waterSite && (
          <WaterSiteDetail waterSite={waterSite} onChange={refetch} />
        )}
      </ThirdPaneContent>
    </ThirdPane>
  );
}
